/**
 * useWizardState Hook
 *
 * Manages wizard state with dual persistence:
 * 1. LocalStorage - Immediate save for offline/fast access
 * 2. Firestore - Synced when authenticated for cross-device continuity
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { doc, setDoc, getDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  WizardState,
  WizardStep,
  WizardMode,
  WizardBasicsData,
  WizardTradesData,
  WizardScopesData,
  WizardPermitsData,
  WizardDetailsData,
  WizardProjectData,
  WizardStepConfig,
  getWizardSteps,
  SINGLE_PERMIT_STEPS,
  DEVELOPMENT_PROJECT_STEPS,
  createInitialWizardState,
  wizardStateToDraft,
} from '@/types/wizard';

const LOCAL_STORAGE_KEY = 'wizard_draft';
const DEBOUNCE_MS = 1000; // Debounce Firestore writes

// Helper to get the Firestore path for wizard drafts (subcollection under contractors)
const getWizardDraftPath = (contractorId: string, draftId: string) =>
  `contractors/${contractorId}/wizard_drafts/${draftId}`;

/**
 * Recursively removes undefined values from an object.
 * Firestore doesn't accept undefined values, so we need to clean the data before saving.
 */
function removeUndefinedValues<T>(obj: T): T {
  if (obj === undefined) return null as T;
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues) as T;
  }
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v !== undefined)
      .map(([k, v]) => [k, removeUndefinedValues(v)])
  ) as T;
}

interface UseWizardStateOptions {
  autoLoad?: boolean;       // Auto-load existing draft on mount
  syncToFirestore?: boolean; // Enable Firestore sync (default: true if authenticated)
}

interface UseWizardStateReturn {
  // State
  state: WizardState;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;

  // Mode
  mode: WizardMode;
  setMode: (mode: WizardMode) => void;

  // Navigation
  currentStep: WizardStep;
  stepNumber: number;
  totalSteps: number;
  steps: WizardStepConfig[];
  canGoNext: boolean;
  canGoPrev: boolean;
  goToStep: (step: WizardStep) => void;
  goNext: () => void;
  goPrev: () => void;

  // Step data updates
  updateBasics: (data: Partial<WizardBasicsData>) => void;
  updateTrades: (data: Partial<WizardTradesData>) => void;
  updateScopes: (data: Partial<WizardScopesData>) => void;
  updatePermits: (data: Partial<WizardPermitsData>) => void;
  updateDetails: (data: Partial<WizardDetailsData>) => void;
  updateProject: (data: Partial<WizardProjectData>) => void;

  // Persistence
  saveDraft: () => Promise<void>;
  loadDraft: () => Promise<boolean>;
  clearDraft: () => Promise<void>;
  discardChanges: () => void;
  hasDraft: boolean;

  // Wizard completion
  isComplete: boolean;
  getCompletionPercentage: () => number;
}

export function useWizardState(options: UseWizardStateOptions = {}): UseWizardStateReturn {
  const { autoLoad = true, syncToFirestore = true } = options;

  const { user, profile } = useAuth();
  const { toast } = useToast();

  const [state, setState] = useState<WizardState>(createInitialWizardState);
  const [isLoading, setIsLoading] = useState(autoLoad);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Get contractor ID for Firestore
  const getContractorId = useCallback(() => {
    if (!user) return null;
    return profile?.contractorId || user.uid;
  }, [user, profile]);

  // Save to localStorage (immediate)
  const saveToLocalStorage = useCallback((wizardState: WizardState) => {
    try {
      const draft = wizardStateToDraft(wizardState);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draft));

      // Also save full state for restoration
      localStorage.setItem(`${LOCAL_STORAGE_KEY}_full`, JSON.stringify(wizardState));
    } catch (e) {
      console.error('Failed to save to localStorage:', e);
    }
  }, []);

  // Save to Firestore (debounced)
  const saveToFirestore = useCallback(async (wizardState: WizardState) => {
    const contractorId = getContractorId();
    if (!contractorId || !syncToFirestore) return;

    try {
      // Use subcollection path: contractors/{contractorId}/wizard_drafts/{draftId}
      const docRef = doc(db, getWizardDraftPath(contractorId, wizardState.id));
      // Remove undefined values - Firestore doesn't accept them
      const cleanedState = removeUndefinedValues({
        ...wizardState,
        contractorId,
        savedAt: Timestamp.now(),
      });
      await setDoc(docRef, cleanedState);
    } catch (e) {
      console.error('Failed to save to Firestore:', e);
      // Don't throw - localStorage is primary
    }
  }, [getContractorId, syncToFirestore]);

  // Debounced save
  const scheduleSave = useCallback((wizardState: WizardState) => {
    // Immediate localStorage save
    saveToLocalStorage(wizardState);
    setHasDraft(true);

    // Debounced Firestore save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (syncToFirestore && user) {
      saveTimeoutRef.current = setTimeout(() => {
        saveToFirestore(wizardState);
      }, DEBOUNCE_MS);
    }
  }, [saveToLocalStorage, saveToFirestore, syncToFirestore, user]);

  // Update state (no auto-save - user must explicitly save)
  const updateState = useCallback((updater: (prev: WizardState) => WizardState) => {
    setState((prev) => {
      const next = updater(prev);
      next.lastUpdated = new Date().toISOString();
      return next;
    });
    // Mark that there are unsaved changes
    setHasUnsavedChanges(true);
  }, []);

  // Load draft from localStorage or Firestore
  const loadDraft = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // First try full state from localStorage
      const localFullStr = localStorage.getItem(`${LOCAL_STORAGE_KEY}_full`);
      if (localFullStr) {
        const localState = JSON.parse(localFullStr) as WizardState;

        // Check if draft is less than 7 days old
        const draftDate = new Date(localState.lastUpdated);
        const daysSinceUpdate = (Date.now() - draftDate.getTime()) / (1000 * 60 * 60 * 24);

        if (daysSinceUpdate < 7) {
          setState(localState);
          setHasDraft(true);
          setHasUnsavedChanges(false);
          setIsLoading(false);
          return true;
        } else {
          // Draft too old, clear it
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          localStorage.removeItem(`${LOCAL_STORAGE_KEY}_full`);
        }
      }

      // If authenticated, try Firestore
      const contractorId = getContractorId();
      if (contractorId && syncToFirestore) {
        // Note: In a real implementation, you'd query for the user's most recent draft
        // For now, we check if there's a draft in localStorage with an ID
        const draftStr = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (draftStr) {
          const draft = JSON.parse(draftStr);
          // Use subcollection path: contractors/{contractorId}/wizard_drafts/{draftId}
          const docRef = doc(db, getWizardDraftPath(contractorId, draft.id));
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const firestoreState = docSnap.data() as WizardState;
            setState(firestoreState);
            setHasDraft(true);
            setHasUnsavedChanges(false);
            setIsLoading(false);
            return true;
          }
        }
      }

      setHasDraft(false);
      setIsLoading(false);
      return false;
    } catch (e) {
      console.error('Failed to load draft:', e);
      setError('Failed to load draft');
      setIsLoading(false);
      return false;
    }
  }, [getContractorId, syncToFirestore]);

  // Clear draft from both storages
  const clearDraft = useCallback(async () => {
    try {
      // Clear localStorage
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      localStorage.removeItem(`${LOCAL_STORAGE_KEY}_full`);

      // Clear Firestore if authenticated
      const contractorId = getContractorId();
      if (contractorId && syncToFirestore) {
        // Use subcollection path: contractors/{contractorId}/wizard_drafts/{draftId}
        const docRef = doc(db, getWizardDraftPath(contractorId, state.id));
        await deleteDoc(docRef);
      }

      // Reset state
      setState(createInitialWizardState());
      setHasDraft(false);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  }, [getContractorId, state.id, syncToFirestore]);

  // Manual save (for explicit "Save Draft" button)
  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      saveToLocalStorage(state);
      if (syncToFirestore && user) {
        await saveToFirestore(state);
      }
      setHasDraft(true);
      setHasUnsavedChanges(false);
      toast({
        title: 'Draft saved',
        description: 'Your progress has been saved.',
      });
    } catch (e) {
      toast({
        title: 'Failed to save',
        description: 'Could not save your progress. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [state, saveToLocalStorage, saveToFirestore, syncToFirestore, user, toast]);

  // Discard current changes without clearing saved draft
  const discardChanges = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(`${LOCAL_STORAGE_KEY}_full`);

    // Reset state to fresh
    setState(createInitialWizardState());
    setHasDraft(false);
    setHasUnsavedChanges(false);
  }, []);

  // Get current steps based on mode
  const getCurrentSteps = useCallback((): WizardStepConfig[] => {
    return getWizardSteps(state.mode);
  }, [state.mode]);

  // Set mode and update step configuration
  const setMode = useCallback((mode: WizardMode) => {
    const steps = getWizardSteps(mode);
    updateState((prev) => ({
      ...prev,
      mode,
      totalSteps: steps.length,
      // If changing mode, stay on entry or reset to first step after entry
      currentStep: prev.currentStep === 'entry' ? 'entry' : steps[1]?.id || 'entry',
      stepNumber: prev.currentStep === 'entry' ? 1 : 2,
    }));
  }, [updateState]);

  // Navigation
  const goToStep = useCallback((step: WizardStep) => {
    const steps = getCurrentSteps();
    const stepConfig = steps.find((s) => s.id === step);
    if (!stepConfig) return;

    updateState((prev) => ({
      ...prev,
      currentStep: step,
      stepNumber: stepConfig.number,
      visitedSteps: prev.visitedSteps.includes(step)
        ? prev.visitedSteps
        : [...prev.visitedSteps, step],
    }));
  }, [updateState, getCurrentSteps]);

  const goNext = useCallback(() => {
    const steps = getCurrentSteps();
    const currentIndex = steps.findIndex((s) => s.id === state.currentStep);
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];

      updateState((prev) => ({
        ...prev,
        currentStep: nextStep.id,
        stepNumber: nextStep.number,
        completedSteps: prev.completedSteps.includes(prev.currentStep)
          ? prev.completedSteps
          : [...prev.completedSteps, prev.currentStep],
        visitedSteps: prev.visitedSteps.includes(nextStep.id)
          ? prev.visitedSteps
          : [...prev.visitedSteps, nextStep.id],
      }));
    }
  }, [state.currentStep, updateState, getCurrentSteps]);

  const goPrev = useCallback(() => {
    const steps = getCurrentSteps();
    const currentIndex = steps.findIndex((s) => s.id === state.currentStep);
    if (currentIndex > 0) {
      const prevStep = steps[currentIndex - 1];
      goToStep(prevStep.id);
    }
  }, [state.currentStep, goToStep, getCurrentSteps]);

  // Step data updaters
  const updateBasics = useCallback((data: Partial<WizardBasicsData>) => {
    updateState((prev) => ({
      ...prev,
      basics: { ...prev.basics, ...data },
    }));
  }, [updateState]);

  const updateTrades = useCallback((data: Partial<WizardTradesData>) => {
    updateState((prev) => ({
      ...prev,
      trades: { ...prev.trades, ...data },
    }));
  }, [updateState]);

  const updateScopes = useCallback((data: Partial<WizardScopesData>) => {
    updateState((prev) => ({
      ...prev,
      scopes: { ...prev.scopes, ...data },
    }));
  }, [updateState]);

  const updatePermits = useCallback((data: Partial<WizardPermitsData>) => {
    updateState((prev) => ({
      ...prev,
      permits: { ...prev.permits, ...data },
    }));
  }, [updateState]);

  const updateDetails = useCallback((data: Partial<WizardDetailsData>) => {
    updateState((prev) => ({
      ...prev,
      details: { ...prev.details, ...data },
    }));
  }, [updateState]);

  const updateProject = useCallback((data: Partial<WizardProjectData>) => {
    updateState((prev) => ({
      ...prev,
      project: { ...prev.project, ...data },
    }));
  }, [updateState]);

  // Computed values
  const steps = getCurrentSteps();
  const currentIndex = steps.findIndex((s) => s.id === state.currentStep);
  const canGoNext = currentIndex < steps.length - 1;
  const canGoPrev = currentIndex > 0;
  const isComplete = state.completedSteps.length === steps.length - 1; // All but review

  const getCompletionPercentage = useCallback(() => {
    const totalSteps = getCurrentSteps();
    return Math.round((state.completedSteps.length / (totalSteps.length - 1)) * 100);
  }, [state.completedSteps, getCurrentSteps]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && !isInitializedRef.current) {
      isInitializedRef.current = true;
      loadDraft();
    }
  }, [autoLoad, loadDraft]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    state,
    isLoading,
    isSaving,
    error,
    hasUnsavedChanges,

    mode: state.mode,
    setMode,

    currentStep: state.currentStep,
    stepNumber: state.stepNumber,
    totalSteps: state.totalSteps,
    steps,
    canGoNext,
    canGoPrev,
    goToStep,
    goNext,
    goPrev,

    updateBasics,
    updateTrades,
    updateScopes,
    updatePermits,
    updateDetails,
    updateProject,

    saveDraft,
    loadDraft,
    clearDraft,
    discardChanges,
    hasDraft,

    isComplete,
    getCompletionPercentage,
  };
}
